import { cn } from "@monbits/ui";
import { useTheme } from "next-themes";
import * as React from "react";
import logoFullDark from "src/assets/logo-full-dark.svg";
import logoFullLight from "src/assets/logo-full-light.svg";
import logoIcon from "src/assets/logo-icon.svg";
import logoMiniDark from "src/assets/logo-mini-dark.svg";
import logoMiniLight from "src/assets/logo-mini-light.svg";

type LogoVariant = "full" | "mini" | "icon";
type LogoSize = "sm" | "md" | "lg" | "xl";

interface LogoProps {
	variant?: LogoVariant;
	size?: LogoSize;
	className?: string;
}

const sizeClasses: Record<LogoSize, string> = {
	sm: "h-4",
	md: "h-6",
	lg: "h-8",
	xl: "h-10",
};

export function Logo({ variant = "full", size = "md", className }: LogoProps) {
	const { theme, systemTheme } = useTheme();
	const [mounted, setMounted] = React.useState(false);

	React.useEffect(() => {
		setMounted(true);
	}, []);

	const currentTheme = mounted
		? theme === "system"
			? systemTheme
			: theme
		: "light";

	const logoSrc = React.useMemo(() => {
		const isDark = currentTheme === "dark";

		switch (variant) {
			case "icon":
				return logoIcon;
			case "mini":
				return isDark ? logoMiniDark : logoMiniLight;
			default:
				return isDark ? logoFullDark : logoFullLight;
		}
	}, [variant, currentTheme]);

	const altText = React.useMemo(() => {
		switch (variant) {
			case "icon":
				return "Monbits Pay Icon";
			case "mini":
				return "Monbits Pay";
			default:
				return "Monbits Pay Logo";
		}
	}, [variant]);

	if (!mounted) {
		return (
			<div
				className={cn("inline-block", sizeClasses[size], className)}
				style={{ aspectRatio: variant === "icon" ? "1" : "auto" }}
			/>
		);
	}

	return (
		<img
			src={logoSrc}
			alt={altText}
			className={cn("inline-block w-auto", sizeClasses[size], className)}
		/>
	);
}
